import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from './cn';
import type { EdsButtonVariant, EdsSize } from './types';

const VARIANT: Record<EdsButtonVariant, string> = {
  primary:
    'bg-eds-primary text-eds-primary-fg hover:bg-eds-emerald-2 shadow-eds-sm hover:-translate-y-0.5',
  secondary:
    'bg-eds-surface-2 text-eds-fg hover:bg-eds-muted border border-eds-border',
  outline:
    'bg-transparent text-eds-emerald-2 border border-eds-emerald hover:bg-eds-surface-2',
  ghost:
    'bg-transparent text-eds-fg hover:bg-eds-surface-2',
  dark:
    'bg-eds-forest text-white hover:bg-eds-forest-2 shadow-eds-sm',
  destructive:
    'bg-eds-danger text-eds-danger-fg hover:opacity-90 shadow-eds-sm',
};

const SIZE: Record<EdsSize, string> = {
  sm: 'h-9 px-3.5 text-sm gap-1.5 rounded-eds-sm',
  md: 'h-11 px-5 text-sm gap-2 rounded-eds-md',
  lg: 'h-12 px-6 text-base gap-2 rounded-eds-md',
};

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: EdsButtonVariant;
  size?: EdsSize;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /** Full-width block button. */
  block?: boolean;
}

/**
 * EDS Button — Enterprise Design Bible §7.1.
 * Accessible: visible focus-visible ring, disabled + aria-busy on loading,
 * motion respects prefers-reduced-motion via `motion-reduce:transform-none`.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      rightIcon,
      block = false,
      disabled,
      children,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;
    return (
      <button
        ref={ref}
        type={type}
        aria-busy={loading || undefined}
        disabled={isDisabled}
        className={cn(
          'inline-flex items-center justify-center font-eds-body font-semibold',
          'transition-all duration-200 ease-[cubic-bezier(.2,.6,.2,1)] motion-reduce:transition-none motion-reduce:transform-none',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring focus-visible:ring-offset-2 focus-visible:ring-offset-eds-bg',
          'disabled:opacity-40 disabled:pointer-events-none disabled:shadow-none disabled:translate-y-0',
          VARIANT[variant],
          SIZE[size],
          block && 'w-full',
          className,
        )}
        {...props}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          leftIcon
        )}
        {children}
        {!loading && rightIcon}
      </button>
    );
  },
);
Button.displayName = 'EdsButton';

export default Button;
